import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Copy, Edit3, Loader2, Save, Sparkles } from "lucide-react";
import { useAction, useMutation } from "convex/react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import DOMPurify from "dompurify";
import { Id } from "~/convex/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { api } from "~/convex/_generated/api";
import { useToast } from "@/components/ui/use-toast";

interface Hook {
  _id: string;
  hookText: string;
  hookCategory: string;
  hookReasoning: string;
  originalDirection?: string;
  likedAt: number;
  isFilmed?: boolean;
  scriptContent?: string;
}

interface FloatingButtonPosition {
  x: number;
  y: number;
  visible: boolean;
}

interface HookScriptGenerationProps {
  hook: Hook;
  creatorProfileId: Id<"exp__creatorProfile">;
}

export const HookScriptGeneration = ({
  hook,
  creatorProfileId,
}: HookScriptGenerationProps) => {
  const [scriptContent, setScriptContent] = useState("");
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [selectedText, setSelectedText] = useState("");
  const [selectedRange, setSelectedRange] = useState<Range | null>(null);
  const [generationGuidance, setGenerationGuidance] = useState("");
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState("");
  const [buttonPosition, setButtonPosition] = useState<FloatingButtonPosition>({
    x: 0,
    y: 0,
    visible: false,
  });

  // Ref for the floating popup
  const popupRef = useRef<HTMLDivElement>(null);

  const generateHookScript = useAction(api.generate.exp__generateHookScript);
  const rewriteSection = useAction(api.generate.exp__rewriteSection);
  const saveScript = useMutation(api.exp__creatorInsights.exp__saveHookScript);
  const { toast } = useToast();

  // Check if we're editing an existing outline or creating a new one
  const isEditingExisting = hook.scriptContent;

  // Sanitize HTML content to prevent XSS attacks
  const sanitizeHTML = (html: string): string => {
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        "strong",
        "em",
        "br",
        "p",
        "ul",
        "ol",
        "li",
        "h1",
        "h2",
        "h3",
        "h4",
        "h5",
        "h6",
      ],
      ALLOWED_ATTR: [],
      ALLOW_DATA_ATTR: false,
    });
  };

  // Load existing outline on mount and when hook changes
  useEffect(() => {
    // Reset state when hook changes
    setIsRegenerating(false);
    setSelectedText("");
    setSelectedRange(null);
    setGenerationGuidance("");
    setButtonPosition({ x: 0, y: 0, visible: false });

    if (isEditingExisting && hook.scriptContent) {
      // Load existing outline content and sanitize it
      const sanitizedContent = sanitizeHTML(hook.scriptContent);
      setScriptContent(sanitizedContent);
      setLastSavedContent(sanitizedContent);
      setIsGeneratingScript(false);
    } else if (!isGeneratingScript) {
      // Only generate if we aren't already generating
      setScriptContent(""); // Clear any previous content
      setLastSavedContent("");
      setIsGeneratingScript(true);
      handleGenerateScript();
    }
  }, [hook._id]); // Only depend on hook ID to avoid double triggers

  // Add document-level event listener for text selection
  useEffect(() => {
    const handleSelection = (event?: Event) => {
      // Check if the event target is within the popup
      if (event && popupRef.current && event.target instanceof Node) {
        if (popupRef.current.contains(event.target)) {
          return; // Don't hide popup if clicking within it
        }
      }

      const selection = window.getSelection();
      if (!selection || selection.isCollapsed) {
        // Only hide if we're not interacting with the popup
        if (
          !popupRef.current ||
          !document.activeElement ||
          !popupRef.current.contains(document.activeElement)
        ) {
          setButtonPosition((prev) => ({ ...prev, visible: false }));
          setSelectedText("");
          setSelectedRange(null);
          setGenerationGuidance("");
        }
        return;
      }

      const text = selection.toString().trim();

      if (text && text.length > 5) {
        // Check if the selection is within our editor area
        const range = selection.getRangeAt(0);
        const editorElement = document.querySelector("[data-editor-container]");

        // Only show popup if selection is within the editor
        if (
          editorElement &&
          editorElement.contains(range.commonAncestorContainer)
        ) {
          const rect = range.getBoundingClientRect();

          setSelectedText(text);
          setSelectedRange(range.cloneRange()); // Store a copy of the range
          setButtonPosition({
            x: rect.left + rect.width / 2,
            y: rect.top - 80,
            visible: true,
          });
        } else {
          setButtonPosition((prev) => ({ ...prev, visible: false }));
          setSelectedText("");
          setSelectedRange(null);
          setGenerationGuidance("");
        }
      } else {
        // Only hide if we're not interacting with the popup
        if (
          !popupRef.current ||
          !document.activeElement ||
          !popupRef.current.contains(document.activeElement)
        ) {
          setButtonPosition((prev) => ({ ...prev, visible: false }));
          setSelectedText("");
          setSelectedRange(null);
          setGenerationGuidance("");
        }
      }
    };

    // Add event listener to document
    document.addEventListener("mouseup", handleSelection);
    document.addEventListener("selectionchange", handleSelection);

    // Cleanup event listener
    return () => {
      document.removeEventListener("mouseup", handleSelection);
      document.removeEventListener("selectionchange", handleSelection);
    };
  }, []);

  const handleGenerateScript = async () => {
    setIsGeneratingScript(true);
    try {
      const generatedScript = await generateHookScript({
        creatorId: creatorProfileId,
        hookText: hook.hookText,
        previousOutline:
          scriptContent && scriptContent !== "Your outline will appear here..."
            ? scriptContent
            : undefined,
      });

      // Sanitize AI-generated content before setting it
      const sanitizedScript = sanitizeHTML(generatedScript);
      setScriptContent(sanitizedScript);

      toast({
        title: "Content Outline Generated!",
        description:
          "Your content outline is ready. Select any text to edit it with AI assistance.",
      });
    } catch (error) {
      console.error("Error generating script:", error);

      // Check if it's a rate limiting error
      const errorMessage =
        error instanceof ConvexError ? error.data : String(error);
      if (errorMessage.includes("You've reached your limit")) {
        setScriptContent(
          "Rate limit reached. Please wait before generating again.",
        );
        toast({
          title: "Rate Limit Reached",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        const errorMsg = "Failed to generate outline. Please try again.";
        setScriptContent(errorMsg);
        toast({
          title: "Generation Failed",
          description: "Failed to generate content outline. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsGeneratingScript(false);
    }
  };

  const handleContentChange = (content: string) => {
    // Don't update state if we're currently generating a script
    if (isGeneratingScript) {
      return;
    }

    // Handle placeholder text
    if (content === "Your content outline will appear here...") {
      return;
    }

    // Sanitize user input
    const sanitizedContent = sanitizeHTML(content);
    setScriptContent(sanitizedContent);
  };

  const regenerateSelection = async () => {
    if (!selectedText || !selectedRange) {
      return;
    }

    setIsRegenerating(true);
    try {
      const newText = await rewriteSection({
        creatorId: creatorProfileId,
        originalText: selectedText,
        guidance: generationGuidance || undefined,
        sectionType: "main", // Default section type
        fullSectionContent: scriptContent,
      });

      // Sanitize AI-generated replacement text
      const sanitizedNewText = sanitizeHTML(newText);

      // Use Range-based replacement
      try {
        // Check if the range is still valid
        if (selectedRange.collapsed || !selectedRange.commonAncestorContainer) {
          throw new Error("Range is no longer valid");
        }

        // Create a temporary container with the new content
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = sanitizedNewText;

        // Create a document fragment with the new content
        const fragment = document.createDocumentFragment();
        while (tempDiv.firstChild) {
          fragment.appendChild(tempDiv.firstChild);
        }

        selectedRange.deleteContents();
        selectedRange.insertNode(fragment);

        // Get the updated HTML from the editor
        const editorElement = document.querySelector(
          "[data-editor-container] .ql-editor",
        );
        if (editorElement) {
          const updatedHTML = editorElement.innerHTML;
          setScriptContent(updatedHTML);
        } else {
          throw new Error("Could not find editor element");
        }
      } catch (rangeError) {
        console.log("Range-based replacement failed, using string replacement");
        // Fallback to the old approach
        const updatedContent = scriptContent.replace(
          selectedText,
          sanitizedNewText,
        );
        setScriptContent(updatedContent);
      }

      toast({
        title: "Text Regenerated",
        description: "The selected text has been rewritten successfully.",
      });
    } catch (error) {
      console.error("Error regenerating text:", error);

      // Check if it's a rate limiting error
      const errorMessage =
        error instanceof ConvexError ? error.data : String(error);
      if (errorMessage.includes("You've reached your limit")) {
        toast({
          title: "Rate Limit Reached",
          description: errorMessage,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Regeneration Failed",
          description:
            "Failed to regenerate the selected text. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsRegenerating(false);
      setButtonPosition((prev) => ({ ...prev, visible: false }));
      setSelectedText("");
      setSelectedRange(null);
      setGenerationGuidance("");
    }
  };

  const copyToClipboard = async () => {
    try {
      // Convert HTML to plain text for clipboard
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = scriptContent;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      await navigator.clipboard.writeText(plainText);
      toast({
        title: "Copied!",
        description: "Content outline copied to clipboard.",
      });
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Failed to copy content outline to clipboard.",
        variant: "destructive",
      });
    }
  };

  const handleSaveScript = async () => {
    if (!scriptContent.trim()) return;

    try {
      await saveScript({
        creatorId: creatorProfileId,
        hookText: hook.hookText,
        scriptContent: scriptContent,
      });

      setLastSavedContent(scriptContent);

      toast({
        title: "Outline Saved",
        description: "Your content outline has been saved successfully.",
      });
    } catch (error) {
      console.error("Error saving script:", error);
      toast({
        title: "Save Failed",
        description: "Failed to save content outline. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Auto-save when content changes (debounced)
  useEffect(() => {
    if (
      !scriptContent ||
      scriptContent === lastSavedContent ||
      isGeneratingScript ||
      isRegenerating ||
      scriptContent === "Your content outline will appear here..."
    ) {
      return;
    }

    const timeoutId = setTimeout(() => {
      handleSaveScript();
    }, 3000); // Auto-save after 3 seconds of no changes

    return () => {
      clearTimeout(timeoutId);
    };
  }, [scriptContent, lastSavedContent, isGeneratingScript, isRegenerating]);

  const hasUnsavedChanges =
    scriptContent !== lastSavedContent &&
    scriptContent.trim() &&
    scriptContent !== "Your content outline will appear here...";

  return (
    <div className="script-generation-container space-y-6 h-full flex flex-col overflow-y-auto max-h-[80vh] p-4 scrollbar-hide">
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <Edit3 className="w-6 h-6" />
              Content Outline
            </h2>
            <p className="text-muted-foreground">
              Generate and edit your content outline for this hook
              {hasUnsavedChanges && (
                <span className="text-orange-500 ml-2">• Unsaved changes</span>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Hook Card */}
      <Card className="flex-shrink-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Hook</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
            <p className="font-medium text-blue-900 dark:text-blue-100">
              {hook.hookText}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {hook.hookReasoning}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Script Editor */}
      <Card className="flex flex-col ">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              Your Content Outline
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleGenerateScript}
                disabled={isGeneratingScript}
                className="flex items-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Regenerate Outline
              </Button>
              <Button
                variant="outline"
                onClick={copyToClipboard}
                disabled={isGeneratingScript || !scriptContent}
                className="flex items-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy Outline
              </Button>
              <Button
                onClick={handleSaveScript}
                disabled={!hasUnsavedChanges}
                className="flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Outline
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col min-h-0 p-0">
          {isGeneratingScript ? (
            <div className="flex items-center justify-center flex-1 p-6">
              <div className="text-center space-y-4">
                <Loader2 className="w-8 h-8 animate-spin mx-auto" />
                <h3 className="text-lg font-semibold">
                  Generating Your Content Outline
                </h3>
                <p className="text-muted-foreground">
                  Creating a structured outline with talking points for your
                  hook...
                </p>
              </div>
            </div>
          ) : (
            <div
              data-editor-container
              className="flex-1 flex flex-col min-h-0 p-6 overflow-auto scrollbar-hide"
            >
              <RichTextEditor
                value={scriptContent}
                onChange={handleContentChange}
                placeholder="Your content outline will appear here..."
                className="flex-1 min-h-[300px] max-h-[400px]"
              />
            </div>
          )}

          {scriptContent &&
            scriptContent !== "Your content outline will appear here..." &&
            scriptContent !== "Generating your content outline..." && (
              <div className="mt-4 p-4 bg-muted/50 rounded-lg flex-shrink-0">
                <p className="text-sm text-muted-foreground">
                  <strong>💡 Tip:</strong> This is your content roadmap - use
                  these talking points as a guide when filming. Select any text
                  to get AI assistance with improving that section.
                  {hasUnsavedChanges && (
                    <span className="text-orange-500 ml-2">
                      Changes are auto-saved after 3 seconds of inactivity.
                    </span>
                  )}
                </p>
              </div>
            )}
        </CardContent>
      </Card>

      {/* Floating edit button */}
      {buttonPosition.visible && (
        <div
          ref={popupRef}
          className="fixed z-50 bg-background border rounded-lg shadow-lg p-4 space-y-3 w-80"
          style={{
            left: Math.max(10, buttonPosition.x - 160), // Keep within viewport
            top: Math.max(10, buttonPosition.y),
          }}
        >
          <div className="space-y-2">
            <p className="text-sm font-medium">Edit selected text:</p>
            <p className="text-xs text-muted-foreground bg-muted p-2 rounded max-h-20 overflow-y-auto">
              "{selectedText}"
            </p>
          </div>

          <Input
            placeholder="How should this be changed? (optional)"
            value={generationGuidance}
            onChange={(e) => setGenerationGuidance(e.target.value)}
            className="text-sm"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                regenerateSelection();
              }
            }}
          />

          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={regenerateSelection}
              disabled={isRegenerating}
              className="flex-1"
            >
              {isRegenerating ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <Sparkles className="w-3 h-3 mr-1" />
              )}
              Rewrite
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setButtonPosition((prev) => ({ ...prev, visible: false }));
                setSelectedText("");
                setSelectedRange(null);
                setGenerationGuidance("");
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
